import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Lock, GraduationCap, Loader2, Briefcase, Users } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Tenant-scoped login dialog rendered on a school's public landing page.
 * Tabs:
 *  - Staff   → branch_admin / special_access / school_owner via /login
 *  - Parent  → guardian using email + child's birthday (DDMMYYYY) first time
 *
 * Props:
 *  - open / onOpenChange
 *  - slug         : school slug (URL segment)
 *  - displayName  : pretty school name
 *  - palette      : palette object from `lib/landing.js`
 */
export default function SchoolPortalLoginDialog({ open, onOpenChange, slug, displayName, palette, logoBase64 }) {
  const { schoolPortalLogin, parentLogin } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("staff"); // "staff" | "parent"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const accent = palette?.text || "#2563EB";
  const gradient = `linear-gradient(90deg, ${palette?.from || "#3B82F6"}, ${palette?.to || "#1D4ED8"})`;

  const reset = () => {
    setEmail("");
    setPassword("");
    setError("");
    setLoading(false);
    setTab("staff");
  };

  const handleOpenChange = (v) => {
    if (!v) reset();
    onOpenChange?.(v);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = tab === "parent"
      ? await parentLogin(slug, email, password)
      : await schoolPortalLogin(slug, email, password);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error);
      return;
    }
    toast.success(`Welcome back, ${res.user.name}!`);
    onOpenChange?.(false);
    if (tab === "parent") {
      navigate(res.user.must_change_password ? "/parent/change-password" : "/parent", { replace: true });
    } else {
      navigate("/dashboard", { replace: true });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md rounded-3xl border-0 shadow-floating" data-testid="school-portal-login-dialog">
        <DialogHeader>
          {logoBase64 ? (
            <img
              src={logoBase64}
              alt={`${displayName} logo`}
              className="mx-auto w-16 h-16 rounded-2xl object-cover shadow-floating mb-3 bg-white"
              data-testid="login-dialog-school-logo"
            />
          ) : (
            <div
              className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center shadow-floating mb-3"
              style={{ background: gradient }}
            >
              <GraduationCap className="w-7 h-7 text-white" />
            </div>
          )}
          <DialogTitle className="text-center font-heading text-2xl font-bold tracking-tight">
            Sign in to <span style={{ color: accent }}>{displayName}</span>
          </DialogTitle>
          <DialogDescription className="text-center text-slate-500">
            {tab === "parent" ? "Parents & guardians" : "Staff & admins"}
          </DialogDescription>
        </DialogHeader>

        {/* Tabs */}
        <div className="grid grid-cols-2 bg-slate-100 rounded-2xl p-1 gap-1" role="tablist">
          {[
            { id: "staff", label: "Staff", icon: Briefcase },
            { id: "parent", label: "Parent", icon: Users },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => { setTab(t.id); setError(""); setPassword(""); }}
              className={`flex items-center justify-center gap-1.5 h-10 rounded-xl text-sm font-heading font-semibold transition-all ${
                tab === t.id ? "bg-white shadow-sm text-foreground" : "text-slate-500 hover:text-foreground"
              }`}
              style={tab === t.id ? { color: accent } : {}}
              data-testid={`portal-tab-${t.id}`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        <form onSubmit={onSubmit} className="space-y-4 pt-2" data-testid="school-portal-login-form">
          <div>
            <Label htmlFor="portal-email" className="font-heading">Email</Label>
            <div className="relative mt-1.5">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="portal-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={tab === "parent" ? "parent@email.com" : "you@school.com"}
                className="rounded-2xl h-12 pl-10"
                style={{ borderColor: `${accent}30` }}
                data-testid="portal-email-input"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="portal-password" className="font-heading">
              Password
            </Label>
            <div className="relative mt-1.5">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="portal-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={tab === "parent" ? "First-time: child's birthday DDMMYYYY" : "••••••••"}
                className="rounded-2xl h-12 pl-10"
                style={{ borderColor: `${accent}30` }}
                data-testid="portal-password-input"
              />
            </div>
            {tab === "parent" && (
              <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
                First-time logging in? Use your child's birthday in{" "}
                <span className="font-semibold text-foreground">DDMMYYYY</span> format
                (e.g. <span className="font-mono">25122017</span> for 25 Dec 2017).
                You'll be asked to set a new password right after.
              </p>
            )}
          </div>

          {error && (
            <div
              className="rounded-2xl bg-[#FFEBEE] text-[#C62828] px-4 py-3 text-sm"
              data-testid="portal-login-error"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-full text-white font-heading font-semibold shadow-floating hover:shadow-floating-hover transition-all"
            style={{ background: gradient }}
            data-testid="portal-login-submit-btn"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Signing in…</>
            ) : "Sign in"}
          </Button>

          {tab === "staff" && (
            <p className="text-xs text-slate-400 text-center pt-1">
              School owners &amp; super admins may also use the{" "}
              <a href="/login" className="font-semibold hover:underline" style={{ color: accent }}>
                main SchoolBoss login
              </a>.
            </p>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
